/**
 * "Where do I start today?" — the auditor's front door.
 *
 * The open and blocking exception counts across the firm, what needs
 * attention, and the most recently touched audits. Items another role must act
 * on (files waiting on intake) are shown only to someone who can act on them.
 */
import { useModule } from '../features/modules/useModule';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { useDashboardQuery } from '../queries/misc';
import { useAuth } from '../provider/auth';
import { PageHeader } from '../provider/pageTitle';
import { count } from '../utils/format';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
// Used by the exception tiles, hidden while the Exceptions tab is.
// import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined'
// import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import { Card, ErrorNotice, Stat, StatusBadge } from '../shared/ui';
import {
	DataTable,
	useFullScreen,
	type Column,
} from '../features/shared/tables';
import { LoadingState } from '../features/shared/states';

/** Where each attention item leads, and who can act on it. */
const ATTENTION: Record<
	string,
	{ to: string; needs: 'accessAudits' | 'accessIntake' }
> = {
	BLOCKING_EXCEPTION: { to: '/audits', needs: 'accessAudits' },
	FILE_FAILED: { to: '/intake', needs: 'accessIntake' },
	FILE_NEEDS_MAPPING: { to: '/intake', needs: 'accessIntake' },
};

export default function Dashboard() {
	const navigate = useNavigate();
	const recentFullScreen = useFullScreen('Recent audits');
	const { permissions } = useAuth();
	/* Polls only while something is processing, then stops. */
	const [active, setActive] = useState(true);
	const { module, link } = useModule();
	const { data, error, isPending, refetch } = useDashboardQuery(
		active,
		module,
	);

	useEffect(() => {
		if (data) setActive(data.counts.filesProcessing > 0);
	}, [data]);

	if (isPending) return <LoadingState label="Loading…" />;
	if (error) return <ErrorNotice error={error} onRetry={refetch} />;
	if (!data) return null;

	const { counts, attention, audits } = data;
	/* An item only shows to someone who can do something about it. */
	const actionable = attention.filter((item) => {
		// Hidden while the Exceptions tab is.
		if (item.kind === 'BLOCKING_EXCEPTION') return false;
		const rule = ATTENTION[item.kind];
		return rule ? Boolean(permissions?.[rule.needs]) : true;
	});

	const columns: Array<Column<(typeof audits)[number]>> = [
		{
			key: 'audit',
			label: 'Audit',
			width: 240,
			sortValue: (row) => row.name,
			render: (row) => (
				<Link
					to={link(`/audits/${row.pkAuditId}`)}
					className="link b"
					onClick={(e) => e.stopPropagation()}
				>
					{row.name}
				</Link>
			),
		},
		{
			key: 'client',
			label: 'Client',
			width: 200,
			sortValue: (row) => row.clientName,
			render: (row) => row.clientName,
		},
		{
			key: 'status',
			label: 'Status',
			width: 150,
			sortValue: (row) => row.status,
			render: (row) => (
				<StatusBadge group="auditStatus" value={row.status} />
			),
		},
		/* Open exceptions hidden while the Exceptions tab is.
    {
      key: 'exceptions',
      label: 'Open exceptions',
      num: true,
      width: 140,
      sortValue: (row) => row.openExceptions,
      render: (row) => count(row.openExceptions),
    },
    */
	];

	return (
		<Box className="stack">
			<PageHeader
				title="Dashboard"
				actions={
					permissions?.accessAudits ? (
						<Button
							component={Link}
							variant="contained"
							to={link('/audits/new')}
						>
							New audit
						</Button>
					) : undefined
				}
			/>

			<Box className="grid g3">
				<Stat
					label="Audits in progress"
					value={count(counts.activeAudits)}
					meta="Not yet issued"
					icon={<AssignmentOutlinedIcon />}
				/>
				{/* Exception tiles hidden while the Exceptions tab is.
        <Stat
          label="Open exceptions"
          value={count(counts.openExceptions)}
          meta="Across every audit"
          icon={<ReportProblemOutlinedIcon />}
          tone={counts.openExceptions > 0 ? 'warn' : undefined}
        />
        <Stat
          icon={<BlockOutlinedIcon />}
          label="Blocking exceptions"
          value={count(counts.blockingExceptions)}
          meta={
            counts.blockingExceptions > 0
              ? 'Stopping a report from being issued'
              : 'Nothing is blocking a report'
          }
          tone={counts.blockingExceptions > 0 ? 'danger' : undefined}
        />
        */}
			</Box>

			{actionable.length > 0 && (
				<Card title="Needs your attention">
					<Box className="stack">
						{actionable.map((item) => (
							<Link
								key={item.kind}
								to={link(ATTENTION[item.kind]?.to ?? '/audits')}
								className="rowlink"
							>
								<Box
									component="span"
									className="row"
									style={{
										alignItems: 'center',
										gap: 'var(--s3)',
									}}
								>
									<StatusBadge
										group="severity"
										value={item.severity}
									/>
									<Box component="span" className="small">
										<strong>{count(item.count)}</strong>{' '}
										{item.label}
									</Box>
								</Box>
								<Box component="span" className="small mut">
									Open
								</Box>
							</Link>
						))}
					</Box>
				</Card>
			)}

			<Card
				title="Recent audits"
				/* Full screen sits beside "All audits" in the header, not in a bar of
           its own above the table, so the table starts right under the title. */
				actions={
					<Box display="flex" gap="var(--s2)">
						{recentFullScreen.button}
						<Button
							component={Link}
							variant="outlined"
							size="small"
							to={link('/audits')}
						>
							All audits
						</Button>
					</Box>
				}
			>
				<Box className="card-bd flush">
					<DataTable
						rows={audits}
						columns={columns}
						rowKey={(row) => row.pkAuditId}
						onRowClick={(row) =>
							navigate(link(`/audits/${row.pkAuditId}`))
						}
						caption="Recent audits"
						fullScreen={recentFullScreen}
						empty={
							<span>
								No audits yet.{' '}
								{permissions?.accessAudits && (
									<Link
										to={link('/audits/new')}
										className="link"
									>
										Create the first audit
									</Link>
								)}
							</span>
						}
					/>
				</Box>
			</Card>
		</Box>
	);
}
